/**
 * What stands where: which tiles show ground, which are gaps after death, how a revealed tile is
 * dressed and which silhouettes stand on the mist floor. Decisions 0007 and 0009; the dressing
 * rules are described in `docs/design/world-dressing.md`.
 *
 * The dressing is cosmetic and never touches the rules, but it is deterministic: everything comes
 * from mix32 of the tile and fixed salts, so a seed looks the same on every device and on every
 * visit. Smooth fields (forest density, birch groves, pine stands, meadows) vary over several
 * tiles, so the world has clearings, groves and edges instead of noise. Landmarks are spread out,
 * one at most per region of 7 by 7 tiles, and grow rarer and stranger with distance from the
 * cabin, so there is always a reason to go further.
 */
import { CATALOG } from "@mistsweeper/assets";
import { mix32, TERRAIN_NAMES, Terrain } from "@mistsweeper/core";
import type { WorldSource } from "./world-source.ts";

/** A model name with a variant chosen by a random number: `prefix-N`. */
export type Pick = (prefix: string, r: number) => string;

/**
 * `prefix-N` with N = (r mod n) + 1, where n is how many of `prefix-1`, `prefix-2` and on the
 * model set has in a row, at least 1. So a family's variants are picked from the model file, and
 * a new variant needs no code. Every renderer picks this way (spec/vectors/placement.json).
 */
export function makePick(names: Iterable<string>): Pick {
  const set = new Set(names);
  const variants = new Map<string, number>();
  return (prefix, r) => {
    let n = variants.get(prefix);
    if (n === undefined) {
      n = 0;
      while (set.has(`${prefix}-${n + 1}`)) n += 1;
      n = Math.max(1, n);
      variants.set(prefix, n);
    }
    return `${prefix}-${(r % n) + 1}`;
  };
}

/**
 * How an instance moves in the wind: not at all, like a tree, like grass and reeds, or like a
 * crop, which billows.
 */
export const SWAY = { none: 0, tree: 1, grass: 2, crop: 3 } as const;
export type Sway = (typeof SWAY)[keyof typeof SWAY];

/** One object on a tile: its model, its offset from the tile's centre in tiles, yaw and scale. */
export interface Placement {
  readonly name: string;
  readonly dx: number;
  readonly dy: number;
  /** Turn around the vertical, in radians. */
  readonly yaw: number;
  readonly scale: number;
  readonly sway: Sway;
  /** Height of its foot above the plateau's top: below it on water. 0 when absent. */
  readonly h?: number;
  /**
   * The tiles it stands on along x and along y from its own: 2 for a massif over a block of 2 by
   * 2 tiles (decision 0018), whose mist is the largest of theirs. 1 when absent.
   */
  readonly span?: number;
  /** Drawn as a silhouette in the mist, as what stands on a hidden tile: a thing not found yet. */
  readonly ghost?: boolean;
}

/** How far a water tile's surface lies below the land, so the shore shows a bank. */
export const WATER_DROP = 0.1;

const SALT_DRESS = 0x64726573; // "dres"
const SALT_DENSITY = 0x64656e73; // "dens"
const SALT_BIRCH = 0x62697263; // "birc"
const SALT_PINE = 0x70696e65; // "pine"
const SALT_MEADOW = 0x6d656164; // "mead"
const SALT_REGION = 0x72656769; // "regi"
const SALT_GHOST = 0x67686f73; // "ghos"
const SALT_SWARM = 0x7377726d; // "swrm"
const SALT_DEER = 0x64656572; // "deer"
const SALT_FISH = 0x66697368; // "fish"
const SALT_MASSIF = 0x6d617373; // "mass"
const SALT_ELK = 0x616c6720; // "alg "
const SALT_MILL = 0x6d696c6c; // "mill"
const SALT_HJELL = 0x686a656c; // "hjel"
const SALT_FARM = 0x6661726d; // "farm"
const SALT_CROP = 0x63726f70; // "crop"
/** The crops of the fields, in the order a block's number picks them. */
const CROPS = ["oats", "barley", "rye", "stooks"] as const;
const SALT_STRAND = 0x73747261; // "stra"
/** What stands by the shore, in the order a tile's number picks them. */
const STRAND = ["fishhut", "seamark", "kummel", "flottning", "rockcarving"] as const;
const SALT_YARD = 0x79617264; // "yard"
/** What stands in a farmyard by a field, in the order a tile's number picks them. */
const YARD = ["smithy", "rootcellar", "beehive", "wellsweep", "cart", "barn"] as const;
const SALT_OAK = 0x6f616b20; // "oak "
const SALT_WINDTHROW = 0x77696e64; // "wind"
const SALT_DUCK = 0x616e6420; // "and "
const SALT_EAGLE = 0x6f726e20; // "orn "
const SALT_REINDEER = 0x72656e20; // "ren "
/** The home cabin on the start tile: crossed gable boards and a small roof over the door. */
export const HOME = "iso-house-a2";
/** Landmarks: at most one per region of this many tiles square. */
export const REGION = 7;

const TAU = Math.PI * 2;

/** True when at least one of the eight neighbours is revealed: within the player's reach. */
export function withinReach(world: WorldSource, x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      if ((dx || dy) && world.isRevealed(x + dx, y + dy)) return true;
    }
  }
  return false;
}

/**
 * True when the mist has lifted from the tile: revealed, settled, or, after death, within
 * reach and without a banner, so the player sees the ground there was and the chasms in it,
 * each with the creature that hid there.
 */
export function shown(world: WorldSource, x: number, y: number): boolean {
  if (world.isRevealed(x, y) || world.isSettled(x, y)) return true;
  if (world.isFallen?.(x, y)) return true;
  if (world.status !== "dead" || world.hasBanner(x, y)) return false;
  return withinReach(world, x, y);
}

/** A shown chasm is a gap in the plateau: it has no top, and the columns around it show their walls. */
export function gap(world: WorldSource, x: number, y: number): boolean {
  if (world.isFallen?.(x, y)) return true;
  return world.status === "dead" && world.isChasm(x, y) && !world.hasBanner(x, y);
}

/** The chasm that ended the run, or one a member of a party fell into: its creature has risen. */
export function struck(world: WorldSource, x: number, y: number): boolean {
  if (world.isFallen?.(x, y)) return true;
  return world.killer !== null && world.killer.x === x && world.killer.y === y;
}

export function hasTop(world: WorldSource, x: number, y: number): boolean {
  return shown(world, x, y) && !gap(world, x, y);
}

/**
 * The creatures that live in the chasms, by the chasm's terrain (decision 0012), from the
 * catalog's `creatures.json`. A creature shows only after death, risen out of its gap, so it
 * says nothing while the run lasts.
 */
export const CREATURES: Readonly<Record<Terrain, readonly string[]>> = {
  [Terrain.water]: CATALOG.creatures.water,
  [Terrain.grass]: CATALOG.creatures.grass,
  [Terrain.forest]: CATALOG.creatures.forest,
  [Terrain.mountain]: CATALOG.creatures.mountain,
};
const SALT_CREATURE = 0x63726561; // "crea"

/** The creature in the chasm at x, y: one of its terrain's, the same one every time. */
export function creatureOn(terrain: Terrain, x: number, y: number): string {
  const list = CREATURES[terrain];
  return list[mix32(x, y, SALT_CREATURE) % list.length] as string;
}

/** A creature's kind, which names it: `iso-skogstroll-2` is a skogstroll. */
export function creatureKind(model: string): string {
  return model.replace(/^iso-/, "").replace(/-\d+$/, "");
}

/**
 * True when objects stand on the tile: the cabin, and revealed tiles with no number showing, so
 * numbers always sit on clear ground and the forest grows back over closed numbers.
 */
export function bearsObjects(world: WorldSource, x: number, y: number): boolean {
  if (!world.isRevealed(x, y)) return false;
  return (x === 0 && y === 0) || world.adjacent(x, y) === 0 || world.isClosed(x, y);
}

/** The k-th random number of a tile for a salt, in [0, 1). */
export function rand(x: number, y: number, salt: number, k = 0): number {
  return mix32(x, y, (salt ^ Math.imul(k + 1, 0x9e3779b9)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Value noise over tiles with corners every `cell` tiles, in [0, 1): a field that varies slowly. */
export function field(x: number, y: number, salt: number, cell: number): number {
  const gx = Math.floor(x / cell);
  const gy = Math.floor(y / cell);
  const fx = smooth(x / cell - gx);
  const fy = smooth(y / cell - gy);
  const a = mix32(gx, gy, salt) / 4294967296;
  const b = mix32(gx + 1, gy, salt) / 4294967296;
  const c = mix32(gx, gy + 1, salt) / 4294967296;
  const d = mix32(gx + 1, gy + 1, salt) / 4294967296;
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}

/** How dense the forest is here, 0 to 1: two octaves, so there are groves and clearings. */
export function forestDensity(x: number, y: number): number {
  return 0.7 * field(x, y, SALT_DENSITY, 6) + 0.3 * field(x, y, SALT_DENSITY + 1, 2.5);
}

const NEIGHBOURS4 = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;
const NEIGHBOURS8 = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
] as const;

/** A sequence of random numbers for one tile, so each choice draws the next. */
class Dice {
  private k = 0;
  private readonly x: number;
  private readonly y: number;
  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }
  next(): number {
    const r = rand(this.x, this.y, SALT_DRESS, this.k);
    this.k += 1;
    return r;
  }
  /** A uint32 for choosing a variant. */
  int(): number {
    return Math.floor(this.next() * 4294967296);
  }
  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }
}

/**
 * Spots for `n` objects on a tile: the four quarters in a shuffled order, each jittered, or the
 * centre, jittered, for a single object.
 */
function spots(n: number, dice: Dice): [number, number][] {
  if (n <= 1) return [[dice.range(-0.15, 0.15), dice.range(-0.15, 0.15)]];
  const quarters: [number, number][] = [
    [-0.24, -0.24],
    [0.24, -0.24],
    [-0.24, 0.24],
    [0.24, 0.24],
  ];
  for (let i = quarters.length - 1; i > 0; i -= 1) {
    const j = Math.floor(dice.next() * (i + 1));
    const t = quarters[i] as [number, number];
    quarters[i] = quarters[j] as [number, number];
    quarters[j] = t;
  }
  return quarters
    .slice(0, n)
    .map(([qx, qy]) => [qx + dice.range(-0.08, 0.08), qy + dice.range(-0.08, 0.08)]);
}

/**
 * The landmark of a tile, if this tile is its region's landmark spot: one tile per region of
 * REGION by REGION tiles, chosen by hash, and seven regions in ten have one. What it is depends
 * on the terrain and grows rarer with distance, by the catalog's `landmarks.json` (decision
 * 0019): homesteads close to the cabin, ruins, cairns and runestones further out, churches, mills
 * and stone circles far away.
 */
export function landmarkOn(world: WorldSource, x: number, y: number, pick: Pick): string | null {
  const rx = Math.floor(x / REGION);
  const ry = Math.floor(y / REGION);
  const h = mix32(rx, ry, SALT_REGION);
  if (x !== rx * REGION + (h % REGION) || y !== ry * REGION + ((h >>> 8) % REGION)) return null;
  if (((h >>> 16) & 1023) % 10 >= 7) return null;
  // Distances squared, in integers, so every platform draws the bands at the same tiles.
  const d2 = x * x + y * y;
  if (d2 < 25) return null;
  const r = (h >>> 20) & 4095;
  // The catalog's bands for the spot's terrain, out to its distance: an entry ending in -* is a
  // variant of its family, picked with the same number.
  const options: string[] = [];
  for (const band of CATALOG.landmarks[TERRAIN_NAMES[world.terrain(x, y)]]) {
    for (const entry of band.entries) {
      options.push(entry.endsWith("-*") ? pick(entry.slice(0, -2), r) : entry);
    }
    if (band.within !== null && d2 < band.within) break;
  }
  return options.length > 0 ? (options[r % options.length] as string) : null;
}

/** The yaw that turns a model's +x toward neighbour (dx, dy). */
function facing(dx: number, dy: number): number {
  return Math.atan2(-dy, dx);
}

/**
 * How a tile that bears objects is dressed, in the Nordic line of decisions 0007 and 0009. The
 * cabin has the house; the clearing around it stays open but for grass. Forest tiles hold one to
 * four trees by the density field, spruces, pines and birches by their fields, with undergrowth;
 * grass is meadow with tufts, bushes, junipers, stones, and trees where it meets the forest;
 * mountains have peaks, rock, cairns and runestones with a few pines; water has reeds along its
 * shores and now and then a jetty or a boat. A region's landmark replaces the tile's dressing.
 */
export function objectsOn(
  world: WorldSource,
  x: number,
  y: number,
  pick: Pick,
  options: { readonly blocks?: boolean } = {},
): Placement[] {
  if (options.blocks !== false) {
    const block = blockOn(world, x, y, pick);
    if (block?.tiles.every((t) => bearsObjects(world, t.x, t.y))) {
      return block.x0 === x && block.y0 === y ? [block.placement] : [];
    }
  }
  const out = dressTile(world, x, y, pick);
  // The cabin's clearing and a landmark's tile keep to themselves; other land meets the water
  // with stones and driftwood.
  if (Math.abs(x) <= 1 && Math.abs(y) <= 1) return out;
  if (
    landmarkOn(world, x, y, pick) ||
    millOn(world, x, y, pick) ||
    hjellOn(world, x, y, pick) ||
    strandOn(world, x, y, pick) ||
    fieldOn(world, x, y) ||
    yardOn(world, x, y, pick) ||
    oakOn(world, x, y, pick) ||
    windthrowOn(world, x, y, pick)
  ) {
    return out;
  }
  const animal =
    deerOn(world, x, y, out) ??
    elkOn(world, x, y, out) ??
    reindeerOn(world, x, y, out) ??
    duckOn(world, x, y);
  if (animal) out.push(animal);
  const eagle = eagleOn(world, x, y);
  if (eagle) out.push(eagle);
  if (world.terrain(x, y) !== Terrain.water) out.push(...shoreOn(world, x, y, pick));
  return out;
}

/**
 * A block of 2 by 2 tiles that holds a thing too large for one tile (decision 0018): a massif on
 * the mountains, a ship setting far out on the meadows. And the thing standing on it.
 */
export interface Block {
  /** Its lower corner, the tile that carries the massif. */
  readonly x0: number;
  readonly y0: number;
  readonly tiles: readonly { readonly x: number; readonly y: number }[];
  readonly placement: Placement;
}

/**
 * The block of 2 by 2 tiles a tile belongs to, if it holds a large thing (decision 0018). The
 * world is cut into blocks from even coordinates, so blocks never overlap. On the mountains one
 * block in four holds a massif, and one in forty a hillfort thirty tiles or more from the cabin;
 * on the meadows one in forty-eight holds a ship setting fourteen tiles or more from the cabin,
 * and thirty or more out one in sixty a chieftain's hall and one in sixty a hillfort. All four
 * tiles must share the terrain; never by the cabin or on a region's landmark. The thing stands on the corner where its four tiles meet, turned by
 * quarters. Whether it shows depends on the tiles' reveal (objectsOn): until all four bear
 * objects, each is dressed as its own.
 */
export function blockOn(world: WorldSource, x: number, y: number, pick: Pick): Block | null {
  const x0 = x - (((x % 2) + 2) % 2);
  const y0 = y - (((y % 2) + 2) % 2);
  const h = mix32(x0 / 2, y0 / 2, SALT_MASSIF);
  const tiles = [
    { x: x0, y: y0 },
    { x: x0 + 1, y: y0 },
    { x: x0, y: y0 + 1 },
    { x: x0 + 1, y: y0 + 1 },
  ];
  const terrain = world.terrain(x0, y0);
  // Four times the squared distance of the block's middle from the cabin, in integers: 3600 is
  // thirty tiles out, 784 fourteen.
  const q = (2 * x0 + 1) ** 2 + (2 * y0 + 1) ** 2;
  let prefix: string;
  if (terrain === Terrain.mountain && h % 4 === 0) prefix = "iso-massif";
  else if (terrain === Terrain.mountain && h % 40 === 1 && q >= 3600) prefix = "iso-fornborg";
  else if (terrain === Terrain.grass && h % 48 === 2 && q >= 784) prefix = "iso-shipsetting";
  else if (terrain === Terrain.grass && h % 60 === 7 && q >= 3600) prefix = "iso-stormannagard";
  else if (terrain === Terrain.grass && h % 60 === 11 && q >= 3600) prefix = "iso-fornborg";
  else return null;
  for (const t of tiles) {
    if (world.terrain(t.x, t.y) !== terrain) return null;
    if (Math.abs(t.x) <= 1 && Math.abs(t.y) <= 1) return null;
    if (landmarkOn(world, t.x, t.y, pick)) return null;
  }
  return {
    x0,
    y0,
    tiles,
    placement: {
      name: pick(prefix, h >>> 8),
      dx: 0.5,
      dy: 0.5,
      yaw: ((h >>> 16) % 4) * (Math.PI / 2),
      scale: 1,
      sway: SWAY.none,
      span: 2,
    },
  };
}

/**
 * A deer grazing on the open meadow near the forest: on a tile where one can graze, one such tile
 * in sixteen. Its own random numbers, so the rest of the dressing stays as it was.
 */
export function deerOn(
  world: WorldSource,
  x: number,
  y: number,
  dressing: readonly Placement[],
): Placement | null {
  if (!canGraze(world, x, y, dressing) || rand(x, y, SALT_DEER, 0) >= 1 / 16) return null;
  return deerAt(x, y);
}

/**
 * Whether a deer can graze on a tile: a grass tile at least three tiles from the cabin, with
 * forest within three tiles but none of its eight neighbours, so no crown hangs over it or hides
 * it, and whose dressing is grass tufts at most.
 */
export function canGraze(
  world: WorldSource,
  x: number,
  y: number,
  dressing: readonly Placement[],
): boolean {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 3) {
    return false;
  }
  if (dressing.some((p) => !p.name.startsWith("iso-tuft"))) return false;
  let forest = false;
  for (let dy = -3; dy <= 3; dy += 1) {
    for (let dx = -3; dx <= 3; dx += 1) {
      if (world.terrain(x + dx, y + dy) !== Terrain.forest) continue;
      if (Math.abs(dx) <= 1 && Math.abs(dy) <= 1) return false;
      forest = true;
    }
  }
  return forest;
}

/** The deer of a tile: a little off the centre, facing any way, by the tile's own numbers. */
export function deerAt(x: number, y: number): Placement {
  const r = (k: number) => rand(x, y, SALT_DEER, k);
  return {
    name: "iso-deer-1",
    dx: (r(1) - 0.5) * 0.3,
    dy: (r(2) - 0.5) * 0.3,
    yaw: r(3) * TAU,
    scale: 0.9 + 0.2 * r(4),
    sway: SWAY.none,
  };
}

/**
 * A water mill by the shore: on a meadow tile at least six tiles from the cabin, not a region's
 * landmark, with water on one of its four sides, one such tile in sixty. It stands square to the
 * grid with its wheel and flume, built on its +y side, toward the water, the first water side in
 * the order +x, minus x, +y, minus y.
 */
export function millOn(world: WorldSource, x: number, y: number, pick: Pick): Placement | null {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 6) return null;
  if (rand(x, y, SALT_MILL, 0) >= 1 / 60) return null;
  const water = NEIGHBOURS4.find(([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.water);
  if (!water || landmarkOn(world, x, y, pick)) return null;
  // The yaw that turns the model's +y toward the water: +y stands at (minus sin, minus cos) on
  // the plateau at a yaw, as +x stands at (cos, minus sin).
  const [wx, wy] = water;
  return {
    name: "iso-watermill-1",
    dx: 0,
    dy: 0,
    yaw: Math.atan2(-wx, -wy),
    scale: 1,
    sway: SWAY.none,
  };
}

/**
 * A fish rack by the shore (Daniel, 2026-10-24): on a meadow or mountain tile at least six tiles
 * from the cabin, not a region's landmark nor a water mill, with water on one of its four sides,
 * one such tile in forty. It stands square to the grid with its long side along the shore, turned
 * as the mill is, its +y toward the first water side.
 */
export function hjellOn(world: WorldSource, x: number, y: number, pick: Pick): Placement | null {
  const t = world.terrain(x, y);
  if (t !== Terrain.grass && t !== Terrain.mountain) return null;
  if (Math.max(Math.abs(x), Math.abs(y)) < 6) return null;
  if (rand(x, y, SALT_HJELL, 0) >= 1 / 40) return null;
  const water = NEIGHBOURS4.find(([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.water);
  if (!water || landmarkOn(world, x, y, pick) || millOn(world, x, y, pick)) return null;
  const [wx, wy] = water;
  return {
    name: pick("iso-hjell", Math.floor(rand(x, y, SALT_HJELL, 1) * 4294967296)),
    dx: 0,
    dy: 0,
    yaw: Math.atan2(-wx, -wy),
    scale: 1,
    sway: SWAY.none,
  };
}

/**
 * A thing by the shore (Daniel, 2026-10-24): on a meadow or mountain tile at least six tiles from
 * the cabin, not a region's landmark, a water mill nor a fish rack, with water on one of its four
 * sides, one such tile in thirty: a fishing hut, a sea mark, a kummel, timber for floating or a
 * rock carving, a variant of its family. It stands square to the grid, turned as the mill is, its
 * +y toward the first water side.
 */
export function strandOn(world: WorldSource, x: number, y: number, pick: Pick): Placement | null {
  const t = world.terrain(x, y);
  if (t !== Terrain.grass && t !== Terrain.mountain) return null;
  if (Math.max(Math.abs(x), Math.abs(y)) < 6) return null;
  if (rand(x, y, SALT_STRAND, 0) >= 1 / 30) return null;
  const water = NEIGHBOURS4.find(([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.water);
  if (!water || landmarkOn(world, x, y, pick)) return null;
  if (millOn(world, x, y, pick) || hjellOn(world, x, y, pick)) return null;
  const family = STRAND[Math.floor(rand(x, y, SALT_STRAND, 1) * STRAND.length)];
  const [wx, wy] = water;
  return {
    name: pick(`iso-${family}`, Math.floor(rand(x, y, SALT_STRAND, 2) * 4294967296)),
    dx: 0,
    dy: 0,
    yaw: Math.atan2(-wx, -wy),
    scale: 1,
    sway: SWAY.none,
  };
}

/**
 * A field on the farmland (Daniel, 2026-10-24): on a meadow tile at least four tiles from the
 * cabin where the smooth farmland field, seven tiles to a cell, exceeds 0.75. Its crop is the same
 * over each block of six tiles by six, so neighbouring fields join: oats, barley, rye, or a field
 * after the harvest with its stooks. It fills its tile unturned, the crop billowing, and nothing
 * else stands on it; a landmark, a mill, a fish rack or a thing by the shore takes its place.
 */
export function fieldOn(world: WorldSource, x: number, y: number): Placement | null {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 4) return null;
  if (field(x, y, SALT_FARM, 7) <= 0.75) return null;
  const crop = CROPS[mix32(Math.floor(x / 6), Math.floor(y / 6), SALT_CROP) % CROPS.length];
  return {
    name: `iso-${crop}-1`,
    dx: 0,
    dy: 0,
    yaw: 0,
    scale: 1,
    sway: crop === "stooks" ? SWAY.none : SWAY.crop,
  };
}

/**
 * A farmyard's thing by a field (Daniel, 2026-10-24): on a meadow tile at least four tiles from
 * the cabin that is no field itself, with a field on one of its four sides, one such tile in five:
 * a smithy, a root cellar, beehives, a well sweep, a cart or a barn, a variant of its family,
 * square to the grid and turned by quarters. Never on a region's landmark, a mill, a fish rack or
 * a thing by the shore.
 */
export function yardOn(world: WorldSource, x: number, y: number, pick: Pick): Placement | null {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 4) return null;
  if (rand(x, y, SALT_YARD, 0) >= 1 / 5 || fieldOn(world, x, y)) return null;
  if (!NEIGHBOURS4.some(([dx, dy]) => fieldOn(world, x + dx, y + dy))) return null;
  if (landmarkOn(world, x, y, pick) || millOn(world, x, y, pick)) return null;
  if (hjellOn(world, x, y, pick) || strandOn(world, x, y, pick)) return null;
  const family = YARD[Math.floor(rand(x, y, SALT_YARD, 1) * YARD.length)];
  return {
    name: pick(`iso-${family}`, Math.floor(rand(x, y, SALT_YARD, 2) * 4294967296)),
    dx: 0,
    dy: 0,
    yaw: Math.floor(rand(x, y, SALT_YARD, 3) * 4) * (Math.PI / 2),
    scale: 1,
    sway: SWAY.none,
  };
}

/**
 * A lone broadleaf on the meadow (Daniel, 2026-10-24): on a meadow tile at least five tiles from
 * the cabin, one such tile in forty, a rowan where the forest is near, on one of its eight
 * neighbours, and elsewhere an oak, the great tree of the old pastures. It has its tile to itself
 * but for tufts, a little off the middle and turned any way. Never on a field or where a
 * landmark, a mill, a fish rack, a thing by the shore or a farmyard's thing stands.
 */
export function oakOn(world: WorldSource, x: number, y: number, pick: Pick): Placement | null {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 5) return null;
  if (rand(x, y, SALT_OAK, 0) >= 1 / 40 || fieldOn(world, x, y)) return null;
  if (landmarkOn(world, x, y, pick) || millOn(world, x, y, pick)) return null;
  if (hjellOn(world, x, y, pick) || strandOn(world, x, y, pick) || yardOn(world, x, y, pick)) {
    return null;
  }
  const r = (k: number) => rand(x, y, SALT_OAK, k);
  const edge = NEIGHBOURS8.some(([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.forest);
  return {
    name: pick(edge ? "iso-rowan" : "iso-oak", Math.floor(r(1) * 4294967296)),
    dx: (r(2) - 0.5) * 0.2,
    dy: (r(3) - 0.5) * 0.2,
    yaw: r(4) * TAU,
    scale: 0.9 + 0.2 * r(5),
    sway: SWAY.tree,
  };
}

/**
 * A spruce the storm threw down (Daniel, 2026-10-24): on a forest tile at least five tiles from
 * the cabin that is no region's landmark, one in fifty, a windthrow lies in place of the tile's
 * trees, square to the grid and turned by quarters.
 */
export function windthrowOn(
  world: WorldSource,
  x: number,
  y: number,
  pick: Pick,
): Placement | null {
  if (world.terrain(x, y) !== Terrain.forest || Math.max(Math.abs(x), Math.abs(y)) < 5) {
    return null;
  }
  if (rand(x, y, SALT_WINDTHROW, 0) >= 1 / 50 || landmarkOn(world, x, y, pick)) return null;
  return {
    name: pick("iso-windthrow", Math.floor(rand(x, y, SALT_WINDTHROW, 1) * 4294967296)),
    dx: 0,
    dy: 0,
    yaw: Math.floor(rand(x, y, SALT_WINDTHROW, 2) * 4) * (Math.PI / 2),
    scale: 1,
    sway: SWAY.none,
  };
}

/**
 * An elk in the forest's edge: on a grass tile at least three tiles from the cabin with forest on
 * at least one of its eight neighbours, so it stands where the trees open, and nothing on it but
 * tufts; one such tile in twenty-four. A deer needs the forest farther off, so the two never
 * share a tile. Its own random numbers, so the rest of the dressing stays as it was.
 */
export function elkOn(
  world: WorldSource,
  x: number,
  y: number,
  dressing: readonly Placement[],
): Placement | null {
  if (world.terrain(x, y) !== Terrain.grass || Math.max(Math.abs(x), Math.abs(y)) < 3) return null;
  if (dressing.some((p) => !p.name.startsWith("iso-tuft"))) return null;
  if (rand(x, y, SALT_ELK, 0) >= 1 / 24) return null;
  const edge = NEIGHBOURS8.some(([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.forest);
  // The elk is large: kept nearer the middle, so its grazing head stays on its own tile.
  return edge ? animalAt("iso-elk-1", x, y, SALT_ELK, 0.1) : null;
}

/**
 * A reindeer on the open fell: on a mountain tile at least three tiles from the cabin whose
 * dressing holds nothing taller than pebbles, rock and tufts, no peak, outcrop or stone; one
 * such tile in six.
 */
export function reindeerOn(
  world: WorldSource,
  x: number,
  y: number,
  dressing: readonly Placement[],
): Placement | null {
  if (world.terrain(x, y) !== Terrain.mountain || Math.max(Math.abs(x), Math.abs(y)) < 3) {
    return null;
  }
  if (dressing.some((p) => !/^iso-(pebbles|rock|tuft)-/.test(p.name))) return null;
  if (rand(x, y, SALT_REINDEER, 0) >= 1 / 6) return null;
  return animalAt("iso-reindeer-1", x, y, SALT_REINDEER);
}

/**
 * A duck on a lake near its shore: on a water tile at least three tiles from the cabin with land
 * on one of its eight neighbours, one such tile in ten, at the water's level. It swims round its
 * place, stops, looks about and upends to feed: the duck model's own motion, dabble.
 */
export function duckOn(world: WorldSource, x: number, y: number): Placement | null {
  if (world.terrain(x, y) !== Terrain.water || Math.max(Math.abs(x), Math.abs(y)) < 3) return null;
  if (rand(x, y, SALT_DUCK, 0) >= 1 / 10) return null;
  const shore = NEIGHBOURS8.some(([dx, dy]) => world.terrain(x + dx, y + dy) !== Terrain.water);
  // Its heading is its swim in the model shader, so it is placed with no turn.
  return shore ? { ...animalAt("iso-duck-1", x, y, SALT_DUCK), yaw: 0, h: -WATER_DROP } : null;
}

/**
 * An eagle soaring over the fells: over a mountain tile at least five tiles from the cabin, one
 * in forty. The model circles its tile's centre high above the trees by its own motion, soar; its beat comes
 * from its place, so its yaw here is 0.
 */
export function eagleOn(world: WorldSource, x: number, y: number): Placement | null {
  if (world.terrain(x, y) !== Terrain.mountain || Math.max(Math.abs(x), Math.abs(y)) < 5) {
    return null;
  }
  if (rand(x, y, SALT_EAGLE, 0) >= 1 / 40) return null;
  return { name: "iso-eagle-1", dx: 0, dy: 0, yaw: 0, scale: 1, sway: SWAY.none };
}

/**
 * A wild animal on a tile: up to `off` from the centre, facing any way, by the tile's own
 * numbers.
 */
function animalAt(name: string, x: number, y: number, salt: number, off = 0.15): Placement {
  const r = (k: number) => rand(x, y, salt, k);
  return {
    name,
    dx: (r(1) - 0.5) * 2 * off,
    dy: (r(2) - 0.5) * 2 * off,
    yaw: r(3) * TAU,
    scale: 0.9 + 0.2 * r(4),
    sway: SWAY.none,
  };
}

/** A spot on the ring along a tile's edges, `inset` from them, chosen by two random numbers. */
function onRing(u: number, v: number, inset: number): [number, number] {
  const e = 0.5 - inset;
  const t = (v * 2 - 1) * e;
  const side = Math.floor(u * 4);
  return side === 0 ? [e, t] : side === 1 ? [-e, t] : side === 2 ? [t, e] : [t, -e];
}

/**
 * Shore detail on a land tile beside water: flat stones and now and then driftwood along the
 * edge toward the water, so the shore reads as a beach.
 */
function shoreOn(world: WorldSource, x: number, y: number, pick: Pick): Placement[] {
  const out: Placement[] = [];
  let k = 500;
  const r = () => rand(x, y, SALT_DRESS, k++);
  for (const [dx, dy] of NEIGHBOURS4) {
    if (world.terrain(x + dx, y + dy) !== Terrain.water) continue;
    const u = r();
    if (u > 0.55) continue;
    const along = (r() * 2 - 1) * 0.3;
    const name =
      u < 0.08 ? "iso-driftwood-1" : pick("iso-shorestones", Math.floor(r() * 4294967296));
    out.push({
      name,
      dx: dx * 0.38 + dy * along,
      dy: dy * 0.38 + dx * along,
      yaw: r() * TAU,
      scale: 0.85 + 0.3 * r(),
      sway: SWAY.none,
    });
  }
  return out;
}

/**
 * Low ground detail on a revealed tile whose number shows: a few grass tufts, pebbles or, on the
 * water, lily pads, only along the ring of the tile's edges, so the number in the middle stays on
 * clear ground (decided by Daniel, 2026-10-14). Nothing here stands taller than a tuft.
 */
export function edgeDetailOn(world: WorldSource, x: number, y: number, pick: Pick): Placement[] {
  const out: Placement[] = [];
  let k = 700;
  const r = () => rand(x, y, SALT_DRESS, k++);
  const t = world.terrain(x, y);
  const n = Math.floor(r() * (t === Terrain.grass ? 4 : 3));
  for (let i = 0; i < n; i += 1) {
    const [dx, dy] = onRing(r(), r(), 0.1);
    const u = r();
    let name: string;
    if (t === Terrain.water) name = pick("iso-lilypads", Math.floor(r() * 4294967296));
    else if (t === Terrain.mountain ? u < 0.6 : u < 0.2)
      name = pick("iso-pebbles", Math.floor(r() * 4294967296));
    else name = pick("iso-tuft", Math.floor(r() * 4294967296));
    const water = t === Terrain.water;
    out.push({
      name,
      dx,
      dy,
      yaw: r() * TAU,
      scale: 0.8 + 0.3 * r(),
      sway: water || name.includes("pebbles") ? SWAY.none : SWAY.grass,
      h: water ? -WATER_DROP : 0,
    });
  }
  if (t !== Terrain.water) out.push(...shoreOn(world, x, y, pick));
  return out;
}

function dressTile(world: WorldSource, x: number, y: number, pick: Pick): Placement[] {
  const out: Placement[] = [];
  const dice = new Dice(x, y);
  let level = 0;
  const put = (name: string, dx: number, dy: number, scale = 1, sway: Sway = SWAY.none) =>
    out.push({ name, dx, dy, yaw: dice.next() * TAU, scale, sway, h: level });
  const natural = () => dice.range(0.85, 1.15);
  const tufts = (n: number) => {
    for (let i = 0; i < n; i += 1) {
      put(
        pick("iso-tuft", dice.int()),
        dice.range(-0.4, 0.4),
        dice.range(-0.4, 0.4),
        natural(),
        SWAY.grass,
      );
    }
  };

  if (x === 0 && y === 0) {
    out.push({ name: HOME, dx: 0, dy: 0, yaw: 0, scale: 1, sway: SWAY.none });
    return out;
  }
  if (x * x + y * y <= 2) {
    tufts(Math.floor(dice.range(0, 3)));
    return out;
  }

  const landmark = landmarkOn(world, x, y, pick);
  if (landmark) {
    // Buildings stand square to the grid, turned by a quarter now and then.
    const quarter = Math.floor(dice.next() * 4) * (Math.PI / 2);
    out.push({ name: landmark, dx: 0, dy: 0, yaw: quarter, scale: 1, sway: SWAY.none });
    tufts(Math.floor(dice.range(0, 3)));
    return out;
  }

  const mill =
    millOn(world, x, y, pick) ?? hjellOn(world, x, y, pick) ?? strandOn(world, x, y, pick);
  if (mill) {
    out.push(mill);
    tufts(Math.floor(dice.range(0, 2)));
    return out;
  }

  const crop = fieldOn(world, x, y);
  if (crop) {
    out.push(crop);
    return out;
  }

  // A thing that has its tile to itself: by a field, alone on the meadow, thrown in the forest.
  const lone =
    yardOn(world, x, y, pick) ?? oakOn(world, x, y, pick) ?? windthrowOn(world, x, y, pick);
  if (lone) {
    out.push(lone);
    tufts(Math.floor(dice.range(0, 2)));
    return out;
  }

  const t = world.terrain(x, y);
  if (t === Terrain.forest) {
    const density = forestDensity(x, y);
    const n = Math.max(1, Math.min(4, 1 + Math.floor(density * 4.2)));
    const birches = field(x, y, SALT_BIRCH, 7);
    const pines = field(x, y, SALT_PINE, 6);
    const birchShare = birches > 0.62 ? 0.7 : 0.12;
    const pineShare = pines > 0.6 ? 0.55 : 0.08;
    for (const [dx, dy] of spots(n, dice)) {
      const u = dice.next();
      let prefix = "iso-spruce";
      if (u < 0.04) prefix = "iso-snag";
      else if (u < 0.04 + birchShare) prefix = "iso-birch";
      else if (u < 0.04 + birchShare + pineShare) prefix = "iso-pine";
      put(pick(prefix, dice.int()), dx, dy, natural(), SWAY.tree);
    }
    const under = dice.next();
    const ux = dice.range(-0.3, 0.3);
    const uy = dice.range(-0.3, 0.3);
    if (under < 0.25) put(pick("iso-fern", dice.int()), ux, uy, natural(), SWAY.grass);
    else if (under < 0.4) put(pick("iso-bush", dice.int()), ux, uy, natural(), SWAY.grass);
    else if (under < 0.48) put(pick("iso-stump", dice.int()), ux, uy, natural());
    else if (under < 0.52) put("iso-log-1", ux, uy, natural());
    else if (under < 0.7) tufts(1);
    else if (under >= 0.95) put(pick("iso-mushroom", dice.int()), ux, uy, natural());
    return out;
  }

  if (t === Terrain.grass) {
    // Where the meadow meets the forest, the forest reaches out with single trees and shrubs.
    const forestSide = NEIGHBOURS4.find(
      ([dx, dy]) => world.terrain(x + dx, y + dy) === Terrain.forest,
    );
    if (forestSide && dice.next() < 0.4) {
      const u = dice.next();
      const prefix = u < 0.35 ? "iso-birch" : u < 0.65 ? "iso-juniper" : "iso-spruce-6";
      const name = prefix === "iso-spruce-6" ? prefix : pick(prefix, dice.int());
      put(name, forestSide[0] * 0.28, forestSide[1] * 0.28, natural(), SWAY.tree);
    }
    const meadow = field(x, y, SALT_MEADOW, 4);
    tufts(Math.floor(meadow * 4 + dice.next()));
    const u = dice.next();
    const sx = dice.range(-0.3, 0.3);
    const sy = dice.range(-0.3, 0.3);
    if (u < 0.08) put(pick("iso-bush", dice.int()), sx, sy, natural(), SWAY.grass);
    else if (u < 0.13) put(pick("iso-juniper", dice.int()), sx, sy, natural(), SWAY.tree);
    else if (u < 0.19) put(pick("iso-pebbles", dice.int()), sx, sy, natural());
    else if (u < 0.23) put(pick("iso-rock", dice.int()), sx, sy, natural());
    else if (u < 0.26) put(pick("iso-boulder", dice.int()), sx, sy, natural());
    return out;
  }

  if (t === Terrain.mountain) {
    const u = dice.next();
    if (u < 0.45) put(pick("iso-peak", dice.int()), 0, 0, dice.range(0.9, 1.1));
    else if (u < 0.6) put(pick("iso-outcrop", dice.int()), 0, 0, natural());
    else if (u < 0.7)
      put(pick("iso-boulder", dice.int()), dice.range(-0.2, 0.2), dice.range(-0.2, 0.2), natural());
    else if (u < 0.77)
      put(pick("iso-runestone", dice.int()), dice.range(-0.15, 0.15), 0, natural());
    else if (u < 0.83) put(pick("iso-stones", dice.int()), 0, 0, natural());
    else if (u < 0.86) put(pick("iso-cairn", dice.int()), 0, 0, natural());
    const v = dice.next();
    const ex = dice.range(-0.35, 0.35);
    const ey = dice.range(-0.35, 0.35);
    if (v < 0.12) put(pick("iso-pine", dice.int()), ex, ey, natural(), SWAY.tree);
    else if (v < 0.22) put(pick("iso-juniper", dice.int()), ex, ey, natural(), SWAY.tree);
    else if (v < 0.4) put(pick("iso-pebbles", dice.int()), ex, ey, natural());
    else if (v < 0.48) put(pick("iso-rock", dice.int()), ex, ey, natural());
    return out;
  }

  // Water: reeds along the shore, lily pads further out, and now and then a jetty reaching out
  // from the land, or a boat, one boat in four a faering. Everything stands at the water's level.
  level = -WATER_DROP;
  const boat = (r: number) =>
    r % 4 === 3 ? pick("iso-faering", r >>> 2) : pick("iso-boat", r >>> 2);
  const shore = NEIGHBOURS4.filter(([dx, dy]) => world.terrain(x + dx, y + dy) !== Terrain.water);
  const land = shore[0];
  if (dice.next() < 0.3) {
    put(
      pick("iso-lilypads", dice.int()),
      dice.range(-0.25, 0.25),
      dice.range(-0.25, 0.25),
      natural(),
    );
  }
  if (!land) return out;
  const jetty = dice.next();
  if (jetty < 0.06 && world.terrain(x + land[0], y + land[1]) === Terrain.grass) {
    out.push({
      name: "iso-jetty-1",
      dx: 0,
      dy: 0,
      yaw: facing(-land[0], -land[1]),
      scale: 1,
      sway: SWAY.none,
      h: level,
    });
    if (dice.next() < 0.5) put(boat(dice.int()), -land[1] * 0.3, land[0] * 0.3, 1);
    return out;
  }
  for (const [dx, dy] of shore) {
    if (dice.next() < 0.6) {
      const along = dice.range(-0.3, 0.3);
      put(
        pick("iso-reeds", dice.int()),
        dx * 0.36 + dy * along,
        dy * 0.36 + dx * along,
        natural(),
        SWAY.grass,
      );
    }
  }
  if (dice.next() < 0.03) {
    put(boat(dice.int()), dice.range(-0.15, 0.15), dice.range(-0.15, 0.15), 1);
  }
  return out;
}

/** True when no revealed tile is within three tiles: beyond the frontier, where silhouettes stand. */
function farFromRevealed(world: WorldSource, x: number, y: number): boolean {
  for (let dy = -3; dy <= 3; dy += 1) {
    for (let dx = -3; dx <= 3; dx += 1) {
      if (world.isRevealed(x + dx, y + dy)) return false;
    }
  }
  return true;
}

const SILHOUETTE_TREE = /^iso-(spruce|pine|birch|snag)-/;
const SILHOUETTE_PEAK = /^iso-peak-/;

/**
 * Silhouettes in the mist beyond the frontier, where the terrain is forest or mountain and no
 * revealed tile is within three tiles: the world ahead, seen faintly and sparsely. They show what
 * will stand there (Daniel, 2026-10-19: what was seen in the mist should be there when the tile
 * is revealed): a forest tile's own trees, a mountain tile's own peak, and a massif block's
 * massif from its corner tile whenever its four tiles are all beyond the frontier. The dressing
 * asked is the tile's by terrain alone and never asks whether a number will show, so a silhouette
 * says nothing about chasms. Empty when nothing shows.
 */
export function ghostsOn(world: WorldSource, x: number, y: number, pick: Pick): Placement[] {
  const t = world.terrain(x, y);
  if (t !== Terrain.forest && t !== Terrain.mountain) return [];
  // The cheap tests first: the frontier test looks at 49 tiles, and most tiles fail the others.
  if (t === Terrain.mountain) {
    const block = blockOn(world, x, y, pick);
    if (block) {
      if (block.x0 !== x || block.y0 !== y) return [];
      return block.tiles.every((b) => farFromRevealed(world, b.x, b.y)) ? [block.placement] : [];
    }
  }
  const h = mix32(x, y, SALT_GHOST) % 100;
  if (h >= (t === Terrain.forest ? 7 : 14)) return [];
  if (!farFromRevealed(world, x, y)) return [];
  const keep = t === Terrain.forest ? SILHOUETTE_TREE : SILHOUETTE_PEAK;
  return dressTile(world, x, y, pick).filter((p) => keep.test(p.name));
}

/**
 * What stands on a tile by the rules alone, before a renderer adds what its models carry (riders,
 * smoke) and the banners. In this order: a tile the mist still covers shows its silhouettes; a
 * shown tile with no top, a gap after death, holds the creature that hid there; a revealed tile
 * with no number showing bears objects, with gnats and a fish where they come; a revealed tile
 * whose number shows has low ground detail along its edges. A shown tile that is not revealed
 * (settled, or within reach after death) holds nothing. Null for a hidden tile with no
 * silhouette, a shown tile that is not revealed, and edge detail that came out empty; a tile that
 * bears objects always has content, its placements perhaps none. This is what
 * spec/vectors/placement.json pins for every renderer.
 */
export interface TileContent {
  readonly kind: "ghost" | "gap" | "objects" | "edge";
  readonly placements: readonly Placement[];
  /** In a gap: the creature, and whether it ended the run. */
  readonly creature?: { readonly name: string; readonly killer: boolean };
  readonly swarm?: Swarm;
  readonly fish?: FishSpot;
}

export function tileContent(
  world: WorldSource,
  x: number,
  y: number,
  pick: Pick,
): TileContent | null {
  if (!shown(world, x, y)) {
    const ghosts = ghostsOn(world, x, y, pick);
    return ghosts.length > 0 ? { kind: "ghost", placements: ghosts } : null;
  }
  if (!hasTop(world, x, y)) {
    const killer = struck(world, x, y);
    const name = creatureOn(world.terrain(x, y), x, y);
    return { kind: "gap", placements: [], creature: { name, killer } };
  }
  if (!world.isRevealed(x, y)) return null;
  if (!bearsObjects(world, x, y)) {
    const placements = edgeDetailOn(world, x, y, pick);
    return placements.length > 0 ? { kind: "edge", placements } : null;
  }
  const swarm = swarmOn(world, x, y);
  const fish = fishOn(world, x, y);
  return {
    kind: "objects",
    placements: objectsOn(world, x, y, pick),
    ...(swarm ? { swarm } : {}),
    ...(fish ? { fish } : {}),
  };
}

/** A swarm of gnats over a tile: where it hangs, in tiles from the centre, and how many dance. */
export interface Swarm {
  readonly dx: number;
  readonly dy: number;
  /** Height of its centre above the plateau's top. */
  readonly h: number;
  readonly n: number;
}

/**
 * Gnats over a tile that bears objects: always one by the cabin, so
 * there is life from the first frame, over one shore tile of water in four and one grass tile
 * beside water in six, and over one meadow tile in thirty. Like the dressing it says nothing
 * about chasms: only tiles with no number showing carry one, and terrain is public.
 */
export function swarmOn(world: WorldSource, x: number, y: number): Swarm | null {
  const r = (k: number) => rand(x, y, SALT_SWARM, k);
  const n = 7 + Math.floor(r(1) * 6);
  if (x === 0 && y === 0) return { dx: 0.42, dy: -0.42, h: 0.4, n };
  const t = world.terrain(x, y);
  const water = (dx: number, dy: number) => world.terrain(x + dx, y + dy) === Terrain.water;
  const shore = NEIGHBOURS4.some(([dx, dy]) =>
    t === Terrain.water ? !water(dx, dy) : water(dx, dy),
  );
  let chance = 0;
  if (t === Terrain.water) chance = shore ? 0.25 : 0;
  else if (t === Terrain.grass) chance = shore ? 1 / 6 : 1 / 30;
  if (r(0) >= chance) return null;
  const level = t === Terrain.water ? -WATER_DROP : 0;
  return { dx: (r(2) - 0.5) * 0.5, dy: (r(3) - 0.5) * 0.5, h: level + 0.4 + 0.25 * r(4), n };
}

/** A fish under a water tile: where on the tile it leaps, which way, and its own slow beat. */
export interface FishSpot {
  readonly dx: number;
  readonly dy: number;
  /** The way it leaps, in radians in the plane. */
  readonly heading: number;
  /** Seconds from one leap to the next, and where in that cycle the clock starts, 0 to 1. */
  readonly period: number;
  readonly phase: number;
}

/**
 * A fish in one water tile in four that bears objects: now and then it leaps out of the water
 * and back, and rings spread where it went in. Like the gnats it says nothing about chasms: only
 * tiles with no number showing carry one.
 */
export function fishOn(world: WorldSource, x: number, y: number): FishSpot | null {
  if (world.terrain(x, y) !== Terrain.water) return null;
  const r = (k: number) => rand(x, y, SALT_FISH, k);
  if (r(0) >= 1 / 4) return null;
  return {
    dx: (r(1) - 0.5) * 0.4,
    dy: (r(2) - 0.5) * 0.4,
    heading: r(3) * TAU,
    period: 30 + 40 * r(4),
    phase: r(5),
  };
}
